<script lang="ts">
	import { page } from '$app/stores';
	import ScrollReveal from '$lib/components/ScrollReveal.svelte';
	import GlowCard from '$lib/components/GlowCard.svelte';
	import MagneticButton from '$lib/components/MagneticButton.svelte';
	import { t, type Language } from '$lib/i18n';

	let lang = $derived($page.params.lang as Language);
	let tr = $derived(t(lang));

	const values = [
		{ titleKey: 'about_value_1', descKey: 'about_value_1_desc' },
		{ titleKey: 'about_value_2', descKey: 'about_value_2_desc' },
		{ titleKey: 'about_value_3', descKey: 'about_value_3_desc' }
	];

	const timelineKeys = [
		{ year: '2016', eventKey: 'about_timeline_2016' },
		{ year: '2020', eventKey: 'about_timeline_2020' },
		{ year: '2024', eventKey: 'about_timeline_2024a' },
		{ year: '2024', eventKey: 'about_timeline_2024b' },
		{ year: '2026', eventKey: 'about_timeline_2026' }
	];
</script>

<svelte:head>
	<title>{tr('about_page_title')}</title>
	<meta name="description" content={tr('about_page_desc')} />
</svelte:head>

<!-- Hero + Story wrapper for seamless aurora -->
<div class="relative">
	<!-- Aurora borealis effect spanning both sections -->
	<div class="aurora-container">
		<div class="aurora-wave wave-1"></div>
		<div class="aurora-wave wave-2"></div>
		<div class="aurora-wave wave-3"></div>
		<div class="aurora-wave wave-4"></div>
	</div>

	<!-- Hero -->
	<section class="relative pt-32 pb-20">
		<div
			class="absolute inset-0 bg-gradient-to-b from-ameta-green/5 via-transparent to-transparent"
		></div>
		<div class="absolute inset-0 bg-grid opacity-20"></div>

		<div class="relative max-w-7xl mx-auto px-6">
			<ScrollReveal>
				<div class="max-w-4xl">
					<p class="text-ameta-green font-medium mb-4 tracking-wide">{tr('about_label')}</p>
					<h1 class="text-5xl md:text-6xl lg:text-7xl font-bold mb-6">
						{tr('about_heading')}
					</h1>
					<p class="text-xl md:text-2xl text-slate-400 leading-relaxed max-w-3xl">
						{tr('about_subtitle')}
					</p>
				</div>
			</ScrollReveal>
		</div>
	</section>

	<!-- Story -->
	<section class="py-20 relative">
		<div class="max-w-7xl mx-auto px-6">
			<div class="grid lg:grid-cols-2 gap-16 items-center">
				<ScrollReveal>
					<h2 class="text-3xl md:text-4xl font-bold mb-6">
						{tr('about_why_title')}
					</h2>
					<div class="space-y-4 text-lg text-slate-400 leading-relaxed">
						<p>{tr('about_why_p1')}</p>
						<p>{tr('about_why_p2')}</p>
						<p>{tr('about_why_p3')}</p>
						<p class="text-slate-300 font-medium">{tr('about_why_p4')}</p>
					</div>
				</ScrollReveal>

				<ScrollReveal delay={150}>
					<div class="relative">
						<div class="absolute -inset-4 bg-ameta-green/10 rounded-3xl blur-3xl"></div>
						<div
							class="timeline-card relative bg-slate-800/50 rounded-2xl border border-slate-700 p-8"
						>
							<h3 class="text-xl font-semibold mb-6 text-slate-200">{tr('about_journey_title')}</h3>
							<div class="space-y-6">
								{#each timelineKeys as item, i}
									<div class="flex gap-4">
										<div class="flex flex-col items-center">
											<div class="w-3 h-3 rounded-full bg-ameta-green"></div>
											{#if i < timelineKeys.length - 1}
												<div class="w-px h-full bg-slate-700 my-2"></div>
											{/if}
										</div>
										<div class="pb-6">
											<p class="text-ameta-green font-mono text-sm">{item.year}</p>
											<p class="text-slate-300">{tr(item.eventKey)}</p>
										</div>
									</div>
								{/each}
							</div>
						</div>
					</div>
				</ScrollReveal>
			</div>
		</div>
	</section>
</div>

<!-- Values -->
<section class="py-20 relative">
	<div class="max-w-7xl mx-auto px-6">
		<ScrollReveal>
			<div class="text-center max-w-3xl mx-auto mb-16">
				<h2 class="text-3xl md:text-4xl font-bold mb-6">
					{tr('about_values_title')}
				</h2>
				<p class="text-lg text-slate-400">
					{tr('about_values_subtitle')}
				</p>
			</div>
		</ScrollReveal>

		<div class="grid md:grid-cols-3 gap-6">
			{#each values as value, i}
				<ScrollReveal delay={i * 100}>
					<GlowCard class="h-full">
						<div class="p-8 h-full">
							<h3 class="text-3xl font-bold text-ameta-green mb-4">{tr(value.titleKey)}</h3>
							<p class="text-slate-400 leading-relaxed">{tr(value.descKey)}</p>
						</div>
					</GlowCard>
				</ScrollReveal>
			{/each}
		</div>

		<!-- Speed fundament -->
		<ScrollReveal delay={300}>
			<div class="mt-6 p-6 md:p-8 rounded-2xl bg-slate-800/50 border border-slate-700">
				<div class="flex flex-col md:flex-row md:items-center gap-4 md:gap-8">
					<h3 class="text-2xl md:text-3xl font-bold text-white shrink-0">
						{tr('about_value_speed')}
					</h3>
					<div class="hidden md:block w-px h-12 bg-slate-600"></div>
					<p class="text-slate-400">{tr('about_value_speed_desc')}</p>
				</div>
			</div>
		</ScrollReveal>
	</div>
</section>

<!-- Team -->
<section class="py-20 relative section-gradient-reverse">
	<div class="max-w-7xl mx-auto px-6">
		<ScrollReveal>
			<div class="text-center max-w-3xl mx-auto">
				<h2 class="text-3xl md:text-4xl font-bold mb-6">
					{tr('about_team_title')}
				</h2>
				<p class="text-lg text-slate-400">
					{tr('about_team_subtitle')}
				</p>
			</div>
		</ScrollReveal>
	</div>
</section>

<!-- Location -->
<section class="py-20 relative">
	<div class="max-w-7xl mx-auto px-6">
		<div class="grid lg:grid-cols-2 gap-12 items-center">
			<ScrollReveal>
				<h2 class="text-3xl md:text-4xl font-bold mb-6">
					{tr('about_location_title')}
				</h2>
				<p class="text-lg text-slate-400 leading-relaxed mb-6">
					{tr('about_location_desc')}
				</p>
				<div class="flex items-center gap-4">
					<div
						class="w-12 h-12 rounded-xl bg-ameta-green/10 flex items-center justify-center text-ameta-green"
					>
						<svg
							aria-hidden="true"
							class="w-6 h-6"
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24"
						>
							<path
								stroke-linecap="round"
								stroke-linejoin="round"
								stroke-width="1.5"
								d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
							/>
							<path
								stroke-linecap="round"
								stroke-linejoin="round"
								stroke-width="1.5"
								d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
							/>
						</svg>
					</div>
					<div>
						<p class="font-medium text-slate-200">Kjoita 40</p>
						<p class="text-slate-400">4630 Kristiansand, Norway</p>
					</div>
				</div>
			</ScrollReveal>

			<ScrollReveal delay={150}>
				<div class="relative h-80 rounded-2xl overflow-hidden border border-slate-700">
					<iframe
						title="Ameta office location"
						width="100%"
						height="100%"
						src="https://www.openstreetmap.org/export/embed.html?bbox=7.9941%2C58.1466%2C8.0141%2C58.1566&layer=mapnik&marker=58.1516%2C8.0041"
						style="border: 0;"
						loading="lazy"
					></iframe>
				</div>
			</ScrollReveal>
		</div>
	</div>
</section>

<!-- CTA -->
<section class="py-24 relative section-gradient">
	<div class="max-w-4xl mx-auto px-6 text-center">
		<ScrollReveal>
			<h2 class="text-3xl md:text-4xl font-bold mb-6">
				{tr('about_cta_title')}
			</h2>
			<p class="text-lg text-slate-400 mb-10 max-w-2xl mx-auto">
				{tr('about_cta_desc')}
			</p>
			<MagneticButton
				href="/{lang}/#contact"
				class="btn-primary text-lg inline-flex items-center gap-3 glow-green"
			>
				<span>{tr('about_cta_button')}</span>
				<svg
					aria-hidden="true"
					class="w-5 h-5"
					fill="none"
					stroke="currentColor"
					viewBox="0 0 24 24"
				>
					<path
						stroke-linecap="round"
						stroke-linejoin="round"
						stroke-width="2"
						d="M17 8l4 4m0 0l-4 4m4-4H3"
					/>
				</svg>
			</MagneticButton>
		</ScrollReveal>
	</div>
</section>

<style>
	.aurora-container {
		position: absolute;
		inset: 0;
		overflow: hidden;
		pointer-events: none;
		mask-image: linear-gradient(to bottom, black 0%, black 70%, transparent 100%);
		-webkit-mask-image: linear-gradient(to bottom, black 0%, black 70%, transparent 100%);
	}

	.aurora-wave {
		position: absolute;
		top: -50%;
		width: 300%;
		height: 200%;
		left: -100%;
		opacity: 0.15;
		filter: blur(100px);
		transform-origin: center top;
	}

	.wave-1 {
		background: linear-gradient(
			180deg,
			transparent 0%,
			rgba(34, 197, 94, 0.06) 20%,
			rgba(16, 185, 129, 0.05) 40%,
			rgba(59, 130, 246, 0.04) 60%,
			transparent 80%
		);
		animation: wave-flow 20s ease-in-out infinite;
	}

	.wave-2 {
		background: linear-gradient(
			180deg,
			transparent 0%,
			rgba(6, 182, 212, 0.05) 25%,
			rgba(139, 92, 246, 0.04) 50%,
			rgba(34, 197, 94, 0.03) 75%,
			transparent 100%
		);
		animation: wave-flow 25s ease-in-out infinite reverse;
		animation-delay: -8s;
	}

	.wave-3 {
		background: linear-gradient(
			180deg,
			transparent 0%,
			rgba(34, 197, 94, 0.04) 30%,
			rgba(59, 130, 246, 0.05) 50%,
			rgba(16, 185, 129, 0.03) 70%,
			transparent 100%
		);
		animation: wave-flow 22s ease-in-out infinite;
		animation-delay: -12s;
		opacity: 0.12;
	}

	.wave-4 {
		background: linear-gradient(
			180deg,
			transparent 10%,
			rgba(139, 92, 246, 0.03) 35%,
			rgba(6, 182, 212, 0.04) 55%,
			rgba(34, 197, 94, 0.05) 75%,
			transparent 90%
		);
		animation: wave-flow 28s ease-in-out infinite;
		animation-delay: -5s;
		opacity: 0.1;
	}

	@keyframes wave-flow {
		0% {
			transform: translateX(-20%) skewX(-20deg) scaleY(1) rotate(-3deg);
		}
		15% {
			transform: translateX(5%) skewX(10deg) scaleY(1.3) rotate(2deg);
		}
		30% {
			transform: translateX(20%) skewX(25deg) scaleY(0.8) rotate(4deg);
		}
		45% {
			transform: translateX(-5%) skewX(-15deg) scaleY(1.2) rotate(-2deg);
		}
		60% {
			transform: translateX(-15%) skewX(-25deg) scaleY(0.9) rotate(-4deg);
		}
		75% {
			transform: translateX(10%) skewX(15deg) scaleY(1.15) rotate(3deg);
		}
		90% {
			transform: translateX(-10%) skewX(-10deg) scaleY(1.05) rotate(-1deg);
		}
		100% {
			transform: translateX(-20%) skewX(-20deg) scaleY(1) rotate(-3deg);
		}
	}
</style>
